// src/AppInitializer.js
import { useEffect, useRef } from "react";
import { useSetRecoilState } from "recoil";
import {
  notificationsState,
  messageState,
  tradesState,
  notificationWSStatusState,
} from "./Recoil/atom";
import api from "./api";
import useNotifications from "./Hooks/useNotifications";
import { getLoggedinUser } from "./helpers/api_helper";

function AppInitializer() {
  const setNotifications = useSetRecoilState(notificationsState);
  const setMessages = useSetRecoilState(messageState);
  const setTrades = useSetRecoilState(tradesState);
  const setWSStatus = useSetRecoilState(notificationWSStatusState);

  const refetchLockRef = useRef(false);
  const refetchTimerRef = useRef(null);

  // Initialize theme on app startup
  const initializeTheme = () => {
    const defaultTheme = "default";
    const defaultContentStyle = "theme-default";
    
    const savedTheme = localStorage.getItem("appTheme") || defaultTheme;
    const savedContentStyle = localStorage.getItem("contentStyle") || defaultContentStyle;
    
    // Apply theme to document root immediately
    document.documentElement.setAttribute("data-theme", savedTheme);
    document.documentElement.setAttribute("data-content-style", savedContentStyle);
    
    console.log("Theme initialized:", { theme: savedTheme, contentStyle: savedContentStyle });
  };

  const fetchAll = async () => {
    // Only fetch if user is logged in
    const user = getLoggedinUser();
    if (!user) {
      console.log("AppInitializer: No user logged in, skipping fetch");
      return;
    }

    if (refetchLockRef.current) return;
    refetchLockRef.current = true;
    try {
      const [respNotifs, respMsgs, respTrades] = await Promise.all([
        api.get("/v1/notifications", { withCredentials: true }),
        api.get("/v1/messages/received", { withCredentials: true }),
        api.get("/v1/trades", { withCredentials: true }),
      ]);
      setNotifications(Array.isArray(respNotifs.data) ? respNotifs.data : []);
      setMessages(respMsgs.data?.received_messages || []);
      setTrades(respTrades.data?.trades || []);
    } catch (error) {
      // Silently fail; pages can still fetch their own data
      console.error("AppInitializer fetch failed", error);
      // If unauthorized, clear the auth data
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.warn("AppInitializer: Unauthorized, clearing auth data");
        localStorage.removeItem("authUser");
        localStorage.removeItem("jwtToken");
      }
    } finally {
      refetchLockRef.current = false;
    }
  };

  useEffect(() => {

    // Initial fetch on mount
    fetchAll();
    
    // Apply saved theme and content style on app initialization
    initializeTheme();
    
    // Only run initial fetch if user is logged in
    const user = getLoggedinUser();
    if (user) {
      fetchAll();
    }
    
    return () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSocket: schedule a debounced refetch when any notification event arrives
  const { status } = useNotifications({
    onEvent: () => {
      if (refetchTimerRef.current) return;
      refetchTimerRef.current = setTimeout(() => {
        fetchAll();
        refetchTimerRef.current = null;
      }, 750);
    },
  });

  useEffect(() => {
    setWSStatus(status);
  }, [status, setWSStatus]);

  return null;
}

export default AppInitializer;
